import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Table, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

const Payment = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, formData } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const totalCost = cartItems.reduce((total, item) => {
    const price = parseFloat(item.price.replace(/[^0-9.-]+/g, ""));
    return total + (isNaN(price) ? 0 : price);
  }, 0);

  const createOrder = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://backend-matihaat.onrender.com/api/payment/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: totalCost }),
      });

      const data = await response.json();
      if (data.success && data.order && data.order.id) {
        setLoading(false);
        initializeRazorpay(data.order.id);
      } else {
        console.error("Failed to create order:", data.message);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in order creation:", error);
      setLoading(false);
    }
  };

  const initializeRazorpay = (order_id) => {
    const options = {
      key: 'rzp_live_wDb7Y2XtYhcs7U',
      amount: totalCost * 100,
      currency: 'INR',
      name: 'Maihaat',
      description: 'Order Payment',
      order_id,
      handler: async (response) => {
        const verifyPayload = {
        razorpay_order_id: order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        userId: user.userId,
        user_name: `${formData.firstName} ${formData.lastName}`,
        user_email: formData.email,
        user_phone: formData.phone,
        billing_address: `${formData.streetAddress}${formData.apartment ? `, ${formData.apartment}` : ''}`,
        billing_city: formData.town,
        billing_pincode: formData.postcode,
        billing_state: formData.state,
        cartItems,
        totalPrice: totalCost,
      };

        try {
          const verifyRes = await fetch('https://backend-matihaat.onrender.com/api/payment/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(verifyPayload),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            // Show order details modal
            setOrderDetails(verifyData.order);
            setShowModal(true);
            // Clear the cart via prop function or context
            // if (clearCart) clearCart();
          } else {
            alert("❌ Payment verification failed");
          }
        } catch (err) {
          console.error("Verification error:", err);
        }
      },
      prefill: {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        contact: formData.phone,
      },
      theme: { color: '#F37254' },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => console.log('Razorpay script loaded');
      script.onerror = () => console.error('Failed to load Razorpay script');
      document.body.appendChild(script);
    };

    if (cartItems?.length && formData?.email) {
      loadRazorpayScript();
    }
  }, [formData, cartItems]);

  const handleModalClose = () => {
    setShowModal(false);
    // Redirect user or do something after closing modal
    navigate('/'); // example: back to home
  };

  return (
    <Container className="mt-5">
      <Row>
        <Col md={8}>
          <h3>Payment Details</h3>
          <h4>Shipping Address</h4>
          <Table bordered striped hover>
            <tbody>
              <tr><td><strong>Full Name</strong></td><td>{formData.firstName} {formData.lastName}</td></tr>
              <tr><td><strong>Street Address</strong></td><td>{formData.streetAddress}{formData.apartment && `, ${formData.apartment}`}</td></tr>
              <tr><td><strong>Town / City</strong></td><td>{formData.town}</td></tr>
              <tr><td><strong>State</strong></td><td>{formData.state}</td></tr>
              <tr><td><strong>Postcode</strong></td><td>{formData.postcode}</td></tr>
              <tr><td><strong>Phone</strong></td><td>{formData.phone}</td></tr>
              <tr><td><strong>Email</strong></td><td>{formData.email}</td></tr>
            </tbody>
          </Table>
        </Col>

        <Col md={4}>
          <h4>Order Summary</h4>
          <Table bordered striped hover>
            <thead>
              <tr><th>Product</th><th>Price</th></tr>
            </thead>
            <tbody>
              {cartItems.map((item, index) => (
                <tr key={index}>
                  <td>
                    <img src={item.img} alt={item.name} style={{ width: 50, height: 50, objectFit: 'cover' }} />
                    <span className="ms-3">{item.name}</span>
                  </td>
                  <td>{item.price}</td>
                </tr>
              ))}
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>₹{totalCost}</strong></td>
              </tr>
            </tbody>
          </Table>

          <Button
            variant="success"
            className="w-100 mt-4"
            onClick={createOrder}
            disabled={loading || !cartItems?.length || !formData?.email}
          >
            {loading ? 'Processing Payment...' : 'Pay Now'}
          </Button>
        </Col>
      </Row>

      {/* Order Details Modal */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Order Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Thank you for your order!</p>
          {orderDetails ? (
            <>
              <p><strong>Order ID:</strong> {orderDetails.order_id}</p>
              <p><strong>Status:</strong> {orderDetails.status}</p>
              <p><strong>Total Price:</strong> ₹{orderDetails.total_price}</p>
              <h5>Items:</h5>
              <ul>
                {orderDetails.items.map((item, idx) => (
                  <li key={idx}>
                    ID: {item.item_id} — Qty: {item.quantity} — Price: ₹{item.price}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>Loading order details...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleModalClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Payment;
